import { useState } from 'react'

import { useUnit } from 'effector-react'
import type { InfiniteData } from 'react-query'
import { useQueryClient } from 'react-query'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Notice } from '@/dialogs/CollectionsDialog/CollectionInfo/CollectionUsers/CollectionUserCard/SubscribedUserCard/Notice.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { $creatorId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs'
import { useToggleBlockUser } from '@/modules/chatting/hooks/use-toggle-block-user'
import { useToggleRestrict } from '@/modules/chatting/hooks/use-toggle-restrict'
import EditIcon from '@/modules/chatting/icons/edit.svg'
import PinIcon from '@/modules/chatting/icons/pin.svg'
import type {
  GetListUsersResponse,
  GetListsUsersCountResponse,
  ListUser,
} from '@/modules/chatting/types'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { classNames } from '@/utils/helpers'

import { useCollectionContext } from '../../../../CollectionProvider'
import { ScrollingUserLists } from '../../../../components/ScrollingUserLists'
import { MoreUserActions } from '../UserActionsDropdown'
import { EarningsDetails } from './EarningsDetails'
import { SubscribedUserActions } from './SubscribedUserActions'
import { SubscriptionDetails } from './SubscriptionDetails'

interface Props {
  user: ListUser
  isPinLoading: boolean
  onClickPin: () => void
}

export const SubscribedUserCard = ({
  user,
  isPinLoading,
  onClickPin,
}: Props) => {
  const creatorId = useUnit($creatorId)
  const [descriptionType, setDescriptionType] = useState('subscription')
  const { updateUserInCache, collection } = useCollectionContext()
  const { unblockUser, isUnblockUserLoading, isBlockUserLoading } =
    useToggleBlockUser()
  const queryClient = useQueryClient()

  const { restrict, unrestrict, isRestrictLoading, isUnrestrictLoading } =
    useToggleRestrict()

  const onEditNotice = (notice: string) => {
    updateUserInCache({ ...user, notice })
  }

  const onClickRename = () => {
    openDialog({
      type: DIALOG_TYPE.RENAME_USER,
      user: user,
      onSuccess: (displayName) => updateUserInCache({ ...user, displayName }),
    })
  }

  const onClickEditNotes = () => {
    openDialog({
      type: DIALOG_TYPE.NOTICE,
      notice: user.notice,
      user: user,
      onEdit: onEditNotice,
    })
  }

  const toggleUserInInfiniteList = (
    listKey: 'restricted' | 'blocked',
    user: ListUser,
    isAdded: boolean,
  ) => {
    queryClient.setQueriesData<InfiniteData<GetListUsersResponse> | undefined>(
      ['listUsers', creatorId, listKey],
      (oldData) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          pages: oldData.pages.map((page, index, array) => {
            if (isAdded) {
              if (index === array.length - 1) {
                return {
                  ...page,
                  list: page.list.concat(user),
                }
              } else {
                return page
              }
            } else {
              return {
                ...page,
                list: page.list.filter((u) => u.id !== user.id),
              }
            }
          }),
        }
      },
    )
    queryClient.setQueryData<GetListsUsersCountResponse | undefined>(
      'list-items-count',
      (oldData) => {
        if (!oldData) return oldData

        const list = collection?.id.startsWith('following')
          ? 'subscribers'
          : 'subscriptions'

        return {
          ...oldData,
          [list]: {
            ...oldData[list],
            [listKey]: isAdded
              ? oldData[list][listKey] + 1
              : oldData[list][listKey] - 1,
          },
        }
      },
    )
  }

  const onChangeRestricted = (isRestricted: boolean) => {
    const newUser = { ...user, isRestricted }

    updateUserInCache(newUser)
    toggleUserInInfiniteList('restricted', newUser, isRestricted)
  }

  const onChangeBlocked = (isBlocked: boolean) => {
    const newUser = { ...user, isBlocked }

    updateUserInCache(newUser)
    toggleUserInInfiniteList('blocked', newUser, isBlocked)
  }

  const handleToggleBlock = () => {
    if (user.isBlocked) {
      unblockUser(user.id).then(() => onChangeBlocked(false))
    } else {
      openDialog({
        type: DIALOG_TYPE.BLOCK_USER,
        user,
        onSuccess: () => onChangeBlocked(true),
      })
    }
  }

  const handleToggleRestrict = () => {
    if (user.isRestricted) {
      unrestrict(user.id).then(() => onChangeRestricted(false))
    } else {
      restrict(user.id).then(() => onChangeRestricted(true))
    }
  }

  return (
    <div className="bg-secondary-50 dark:bg-secondary-600 shrink-0 space-y-3 rounded-lg p-3">
      <div className="flex max-w-full items-start justify-between gap-3 overflow-hidden">
        <div className="flex items-center gap-3 overflow-hidden">
          <CreatorAvatar
            name={user.displayName ?? user.name}
            avatarSrc={
              user.avatarThumbs?.c50 ?? user.avatarThumbs?.c144 ?? user.avatar
            }
            size={40}
            className="shrink-0"
          />
          <div className="space-y-1 overflow-hidden">
            <div className="flex items-center gap-2">
              <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium leading-[22px]">
                {!!user.displayName && user.displayName.length > 0
                  ? user.displayName
                  : user.name}
              </p>
              <IconButton
                className="text-secondary-300"
                icon={EditIcon}
                onClick={onClickRename}
              />
            </div>
            <p className="text-secondary-300 text-xs">@{user.username}</p>
          </div>
        </div>
        <div className="flex gap-3">
          {collection?.type === 'custom' && (
            <IconButton
              icon={PinIcon}
              onClick={onClickPin}
              disabled={isPinLoading}
              className={classNames('text-secondary-300', {
                'text-primary-500 [&>svg]:fill-primary-500': user.isPinned,
              })}
            />
          )}
          <MoreUserActions
            user={user}
            // onClickDiscount={handleClickDiscount}
            onClickRename={onClickRename}
            onClickEditNotes={onClickEditNotes}
            onClickRestrict={handleToggleRestrict}
            onClickBlock={handleToggleBlock}
            isBlockLoading={isBlockUserLoading || isUnblockUserLoading}
          />
        </div>
      </div>
      <Notice notice={user.notice} />
      <ScrollingUserLists user={user} />
      <SubscribedUserActions
        user={user}
        onClickEditNotes={onClickEditNotes}
        onClickRestrict={handleToggleRestrict}
        isRestrictActionLoading={isRestrictLoading || isUnrestrictLoading}
      />
      <Tabs
        value={descriptionType}
        onValueChange={setDescriptionType}
        className="mt-6 flex w-full flex-col justify-center"
      >
        <TabsList className="dark:border-secondary-400 grid w-full grid-cols-2 self-center bg-transparent dark:bg-transparent">
          <TabsTrigger
            value="subscription"
            className="data-[state=active]:bg-primary-100 data-[state=active]:text-primary-500 dark:data-[state=active]:bg-primary-900 data-[state=active]:shadow-none"
          >
            Subscription
          </TabsTrigger>
          <TabsTrigger
            className="data-[state=active]:bg-primary-100 data-[state=active]:text-primary-500 dark:data-[state=active]:bg-primary-900 data-[state=active]:shadow-none"
            value="earnings"
          >
            Earnings
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="subscription"
          className="mt-2"
        >
          {user.subscribedOnData && (
            <SubscriptionDetails
              subscription={user.subscribedOnData}
              userId={user.id}
            />
          )}
        </TabsContent>
        <TabsContent
          value="earnings"
          className="mt-2"
        >
          {user.subscribedOnData && (
            <EarningsDetails subscription={user.subscribedOnData} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
